import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { useWorkerStanding, useWorkerScore, useScoreBreakdown } from '../index.js'
import { BridgeOpenShiftCard, biddingDetail, biddingHeadline, useBiddingStatus } from '../../schedule/index.js'
import { HelpIcon, isNotFoundError, Skeleton } from '../../core/index.js'
import type { BiddingStatus } from '../../schedule/index.js'
import type { WorkerStanding } from '../index.js'
import { StandingCard } from '../components/StandingCard.js'
import { WorkerBarriers } from '../../messages/index.js'
import { WorkerBreakdownBars } from '../components/WorkerBreakdownBars.js'
import { Tabs } from '../components/PeriodView.js'
import { WorkerHistory } from './WorkerHistory.js'
import { WorkerContactsCard } from '../../messages/index.js'
import { LoadFailure } from '../../core/index.js'


/**
 * Where bidding stands, as a call to action (the demo's "Bid now for March" banner): coral and bold while the
 * worker has a month to bid for and has not submitted it, quiet otherwise, and a button that opens the Shifts tab.
 * It reads the facility's preferences window, which replaced the per-tier bid window.
 */
function BiddingBanner({ status, onOpenShifts }: { status: BiddingStatus; onOpenShifts?: () => void }) {
  const isOpen = status.kind === 'open' && !status.submitted
  const body = (
    <>
      <div className="min-w-0 text-left">
        <div className={`text-[9px] font-bold uppercase tracking-wide ${isOpen ? 'text-white/80' : 'text-coral'}`}>{status.kind === 'assigned' ? 'Your schedule' : 'Bidding'}</div>
        <div className={`text-xs font-extrabold ${isOpen ? 'text-white' : 'text-ink'}`}>{biddingHeadline(status)}</div>
        <div className={`text-[11px] ${isOpen ? 'text-white/90' : 'text-ink-mid'}`}>{biddingDetail(status)}</div>
      </div>
      {onOpenShifts && <ArrowRight aria-hidden="true" strokeWidth={2.5} className={`h-4 w-4 shrink-0 ${isOpen ? 'text-white' : 'text-coral'}`} />}
    </>
  )
  const className = `flex w-full items-center justify-between gap-3 border p-3 ${isOpen ? 'bg-coral border-coral text-white' : 'bg-coral-lt border-coral/40'}`
  if (!onOpenShifts) {
    return <div className={className}>{body}</div>
  }
  return (
    <button type="button" onClick={onOpenShifts} aria-label={isOpen ? 'Open the Shifts tab to bid' : 'Open the Shifts tab'} className={`${className} ${isOpen ? 'hover:brightness-95' : 'hover:border-coral'}`}>
      {body}
    </button>
  )
}

type HomeView = 'current' | 'history'

/** The same pair the staff scoring panel offers for a worker: this month, and the months before it. */
const HOME_VIEWS: { id: HomeView; label: string }[] = [
  { id: 'current', label: 'Current' },
  { id: 'history', label: 'History' },
]

export interface WorkerHomeProps {
  workerId: string
  /** Opens the Shifts tab, where bidding happens: the bidding band is a button that leads there. */
  onOpenShifts?: () => void
}

/**
 * The worker's home screen, which also holds their score as staff see it:
 * a Current view and a History view (a dial per month). Current runs in
 * the order the plan sets (§2.1): where am I
 * (the standing card: dial, held and on-track tier, next tier, the one
 * thing to work on), what is coming (where bidding stands). Messages have their own
 * Messages tab (the user's call, 18 October 2026: Home no longer lists
 * them), but the note to the facility stays here, beside the standing it
 * is about. What I have done - this month's shifts - has its
 * own Shifts tab (WorkerShifts.tsx). Every number comes from the API;
 * nothing here works a tier out from a score.
 */
export function WorkerHome(props: WorkerHomeProps) {
  const [view, setView] = useState<HomeView>('current')
  return (
    <div>
      <div className="max-w-md mx-auto px-4 pt-4 -mb-5 flex items-center gap-2">
        <div className="flex-1">
          <Tabs tabs={HOME_VIEWS} active={view} onChange={setView} />
        </div>
        <HelpIcon topic="scoring.worker-home" />
      </div>
      {view === 'current' ? <CurrentView {...props} /> : <WorkerHistory workerId={props.workerId} />}
    </div>
  )
}

/** This month: the dial and standing, the bar per behavior behind it, the note to the facility, then what is coming. */
function CurrentView({ workerId, onOpenShifts }: WorkerHomeProps) {
  const standingQuery = useWorkerStanding(workerId)
  const scoreQuery = useWorkerScore(workerId)
  // Where bidding stands never holds the screen up: the band waits for its reads and shows once they arrive.
  const bidding = useBiddingStatus(workerId)
  // For the breakdown bars and the "what's getting in the way" card: it never holds the screen up, and with no breakdown yet there is nothing to show.
  const breakdownQuery = useScoreBreakdown(workerId)

  // A 404 on the score is "nothing scored yet", an expected state for a new worker - not a failure.
  const noScoreYet = scoreQuery.isError && isNotFoundError(scoreQuery.error)
  const isPending = standingQuery.isPending || (scoreQuery.isPending && !noScoreYet)
  const isError = standingQuery.isError || (scoreQuery.isError && !noScoreYet)

  if (isPending) {
    return <Skeleton lines={5} className="p-8" />
  }
  if (isError) {
    return <LoadFailure className="p-8" message="Your standing could not be loaded." onRetry={() => void Promise.all([standingQuery.refetch(), scoreQuery.refetch()])} retrying={standingQuery.isFetching || scoreQuery.isFetching} />
  }

  const standing = standingQuery.data
  const score = noScoreYet ? null : (scoreQuery.data ?? null)

  return (
    <div className="max-w-md mx-auto flex flex-col gap-4 p-4">
      {/* A Clinical Bridge shift waiting for an answer comes before everything: it is the one thing on this screen with a clock on it. Nothing for most workers most days. */}
      <BridgeOpenShiftCard workerId={workerId} />

      {score === null ? <StartingState standing={standing} /> : <StandingCard standing={standing} />}

      {/* What the dial is made of: one bar per behavior, coloured by how it is going. */}
      {score !== null && breakdownQuery.data && breakdownQuery.data.inputs.length > 0 && (
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1.5">Score breakdown</div>
          <div className="bg-white border border-marine/15 p-4">
            <WorkerBreakdownBars score={score} inputs={breakdownQuery.data.inputs} />
          </div>
          <p className="mt-1.5 text-[10px] text-ink-mid/80 leading-relaxed">
            Each bar is one behavior, 0 to 100, from your own shifts: green is going well, gold has room to grow, red needs attention. Under it is what was counted and how much of your score it is. A bar marked not counted is not against you: nobody measured it. Your tier changes once a month, at the close.
          </p>
        </div>
      )}

      {/* Where the worker tells the facility what is getting in the way, beside the standing it is about. */}
      {breakdownQuery.data && <WorkerBarriers workerId={workerId} inputs={breakdownQuery.data.inputs} />}

      {bidding && <BiddingBanner status={bidding} onOpenShifts={onOpenShifts} />}

      {/* How the worker is reached when the app is closed: push on this device, email, texts where offered. */}
      <WorkerContactsCard workerId={workerId} />

      <div className="bg-marine-lt border-l-4 border-marine p-3 text-[10.5px] text-ink-mid leading-relaxed">
        <strong className="text-marine-dk">On approved leave?</strong> Your score is held where it is. You'll come back exactly where you left off — tier and bid eligibility included.
      </div>
    </div>
  )
}

/** A worker with nothing scored yet: no number shown as if it were a score. */
function StartingState({ standing }: { standing: WorkerStanding }) {
  return (
    <div className="bg-white border border-marine/15 p-5 text-center">
      <div className="text-[11px] font-bold text-ink-mid tracking-wide mb-2">
        {standing.name.toUpperCase()} · {standing.role}
      </div>
      <div className="text-sm font-extrabold text-ink">Your score starts with your first shift.</div>
      <div className="text-[11px] text-ink-mid mt-1 leading-relaxed">
        Every shift you work on time and to the end builds it. Nothing counts against you until something is reported.
      </div>
    </div>
  )
}
